import { FC, useState } from 'react';
import {
  useGetFactorDealsQuery,
  useGetFactorsBranchesQuery,
  useGetFactorsDealsStatusesQuery,
} from '@/app/api';
import useFilters from '@/app/hooks/useFilters';
import { EmptyDataPlaceholder } from '@/shared/ui/empty-data-placeholder';
import { UITable } from '@/shared/ui/ui-table';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import { UILoader } from '../../shared/ui/ui-loader';
import { DealStatusChip } from '../../shared/deal-status-chip';
import { DatePicker } from '@mui/x-date-pickers';
import UIAutocomplete from '../../shared/ui/ui-autocmplete';
import { numericFormatter } from 'react-number-format';
import dayjs from 'dayjs';

import { useTranslation } from 'react-i18next';
import {
  Currency,
  FactorDealStatus,
  GetClientsList,
} from '../../app/api/types';
import { Link } from 'react-router-dom';
import {
  checkRole,
  getDealBuyerName,
  getDealSellerName,
  getFetchEror,
  getInternationalDealKind,
  getInternationalDealLabel,
  triada,
} from '@/app/helpers';
import { useNotification } from '@/shared/contexts/NotificationContext';
import { fetcher } from '@/app/api/fetcher';
import ArrowLeftIcon from '@/app/assets/icons/arrow-left.svg?react';
import { CURRENCY_OPTIONS } from '@/constants';
import { useAppSelector } from '@/app/hooks';

interface Props {
  clientsList: GetClientsList[] | undefined;
}

interface Filters {
  dateFrom: string;
  dateTo: string;
  status: FactorDealStatus;
  search: string;
  page: number;
  pageSize: number;
  branchId: number | null;
  currency: Currency;
}

const defaultFilters: Filters = {
  dateFrom: '',
  dateTo: dayjs().format('DD.MM.YYYY'),
  status: '',
  search: '',
  page: 1,
  pageSize: 10,
  branchId: null,
  currency: 'UZS',
};

export const FactorDeals: FC<Props> = ({ clientsList }) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { nonEmptyFilters: filters, updateFilters } = useFilters<Filters>({
    defaultFilters,
    saveToUrl: true,
  });
  const { profile } = useAppSelector((state) => state.auth);

  const { isAdmin } = checkRole(profile?.roles);
  const branchIdFromProfile = profile?.branchId;
  const { data: branches, isLoading: isBranchesLoading } =
    useGetFactorsBranchesQuery();

  const { data, isLoading, isFetching } = useGetFactorDealsQuery({
    ...filters,
    branchId: branchIdFromProfile || filters.branchId || undefined,
    currency: filters.currency || undefined,
  });
  const { data: statuses } = useGetFactorsDealsStatusesQuery({});

  const [isExportLoading, setIsExportLoading] = useState(false);

  if (isLoading || isFetching) {
    return <UILoader />;
  }

  const handleExport = async () => {
    try {
      const { branchId, ...restFilters } = filters;
      setIsExportLoading(true);
      const response = await fetcher('/factors/deals/export', {
        // body: JSON.stringify(filters),
        method: 'POST',
        params: {
          ...restFilters,
          ...(branchId !== null && { branchId }),
        },
        headers: {
          'Content-Type': 'application/json',
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      }).then((res) => {
        return res.blob();
      });

      // Create a link element to download the file
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');

      const filename = 'factor_deals.xlsx';

      link.href = url;
      link.setAttribute('download', filename); // Set the file name
      document.body.appendChild(link);
      link.click(); // Programmatically click the link to trigger the download
      document.body.removeChild(link); // Clean up and remove the link element
      window.URL.revokeObjectURL(url); // Release the object URL
      setIsExportLoading(false);
    } catch (err) {
      showNotification(getFetchEror(err));
    }
  };

  const backUrl = encodeURIComponent(
    window.location.pathname + window.location.search
  );

  return (
    <Stack spacing={2.5}>
      <Stack direction='row' justifyContent='space-between'>
        <Stack direction='row' spacing={3}>
          <Stack direction='row' spacing={1}>
            <DatePicker
              label={t('from') + ':'}
              format='DD.MM.YYYY'
              value={
                dayjs(filters.dateFrom, 'DD.MM.YYYY').isValid()
                  ? dayjs(filters.dateFrom, 'DD.MM.YYYY')
                  : null
              }
              onChange={(value) =>
                updateFilters({
                  dateFrom: dayjs(value).isValid()
                    ? dayjs(value).format('DD.MM.YYYY')
                    : '',
                })
              }
            />
            <DatePicker
              label={t('to') + ':'}
              format='DD.MM.YYYY'
              value={
                dayjs(filters.dateTo, 'DD.MM.YYYY').isValid()
                  ? dayjs(filters.dateTo, 'DD.MM.YYYY')
                  : null
              }
              onChange={(value) =>
                updateFilters({
                  dateTo: dayjs(value).isValid()
                    ? dayjs(value).format('DD.MM.YYYY')
                    : '',
                })
              }
            />
          </Stack>

          <Stack direction='row' spacing={3}>
            <UIAutocomplete
              value={
                statuses?.data.find((item) => item.name === filters.status) ||
                null
              }
              options={statuses?.data ?? []}
              TextFieldProps={{
                label: t('all_statuses'),
                sx: { width: 200 },
              }}
              getOptionLabel={(option) => t(`statuses.factor.${option.name}`)}
              onChange={(_event, value) => {
                updateFilters({ status: value?.name });
              }}
            />

            <UIAutocomplete
              value={
                clientsList?.find((item) => item.tin === filters.search) || null
              }
              options={clientsList ?? []}
              TextFieldProps={{
                label: t('client_name'),
                sx: { width: 200 },
              }}
              getOptionLabel={(option) => option.shortName}
              onChange={(_event, value) => {
                updateFilters({ search: value?.tin });
              }}
            />

            {isAdmin && (
              <UIAutocomplete
                value={
                  branches?.data?.find(
                    (item) => item.unique === filters.branchId
                  ) || null
                }
                options={branches?.data ?? []}
                loading={isBranchesLoading}
                TextFieldProps={{
                  label: t('branch'),
                  sx: { width: 200 },
                }}
                getOptionLabel={(option) => option.branchName}
                onChange={(_event, value) => {
                  updateFilters({ branchId: value?.unique || null });
                }}
              />
            )}

            <UIAutocomplete
              value={
                branches?.data?.find(
                  (item) => item.unique === filters.branchId
                ) || null
              }
              options={branches?.data ?? []}
              loading={isBranchesLoading}
              TextFieldProps={{
                label: t('branch'),
                sx: { width: 200 },
              }}
              getOptionLabel={(option) => option.branchName}
              onChange={(_event, value) => {
                updateFilters({ branchId: value?.unique || null });
              }}
            />

            <UIAutocomplete
              value={CURRENCY_OPTIONS.find(
                (item) => item.value === filters.currency
              )}
              options={CURRENCY_OPTIONS}
              TextFieldProps={{
                label: t('currency'),
                sx: { width: 125 },
              }}
              getOptionLabel={(option) => {
                return t(option.label);
              }}
              onChange={(_event, value) => {
                updateFilters({ currency: value?.value as Currency });
              }}
              disableClearable
            />
          </Stack>
        </Stack>

        <Stack direction='row' spacing={1}>
          <Button
            variant='contained'
            color='inherit'
            onClick={handleExport}
            disabled={isExportLoading}
          >
            {t('export')}
          </Button>
        </Stack>
      </Stack>

      <EmptyDataPlaceholder
        isEmpty={!data?.data.length}
        title={t('no_factor_deals_yet_title')}
        subtitle={t('no_factor_deals_yet_subtitle')}
        isLoading={isLoading}
      >
        <UITable
          data={data?.data ?? []}
          totals={{
            contractAmount: triada(data?.contractAmount || 0, { decimal: 2 }),
            payoutAmount: triada(data?.totalPayoutAmount || 0, { decimal: 2 }),
            factorCommission: triada(data?.factorCommission || 0, {
              decimal: 2,
            }),
          }}
          columns={[
            {
              label: 'ID',
              key: 'unique',
            },
            {
              label: t('date'),
              render: ({ createdAt }) =>
                dayjs(createdAt, 'DD.MM.YYYY').format('DD.MM.YYYY'),
            },
            {
              label: t('status'),
              render: (row) => {
                return <DealStatusChip status={row.status} type='factor' />;
              },
            },
            {
              label: t('factoring_variant'),
              render: (row) => {
                const internationalKind = getInternationalDealKind(row);
                return (
                  <>
                    {internationalKind ? (
                      <Typography fontSize='inherit'>
                        {getInternationalDealLabel(row, internationalKind, t)}
                      </Typography>
                    ) : (
                      row.factoringType && (
                        <Typography fontSize='inherit'>
                          {t(`factoring_types.${row.factoringType}`)}
                        </Typography>
                      )
                    )}
                    {row?.recourseType && (
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        fontSize={12}
                      >
                        {t(`recourse_types.${row.recourseType}`)}
                      </Typography>
                    )}
                  </>
                );
              },
            },
            {
              label: t('buyer'),
              render: (row) => getDealBuyerName(row),
            },
            {
              label: t('supplier'),
              render: (row) => getDealSellerName(row),
            },
            {
              label: t('deal_sum'),
              render: (row) =>
                numericFormatter(String(row.factorDealAmount), {
                  thousandSeparator: ' ',
                }),
              totalKey: 'contractAmount',
              align: 'right',
              headerAlign: 'right',
            },
            {
              label: t('paid'),
              render: (row) =>
                numericFormatter(String(row.payoutAmount), {
                  thousandSeparator: ' ',
                }),
              totalKey: 'payoutAmount',
              align: 'right',
              headerAlign: 'right',
            },
            {
              label: t('comission'),
              render: (row) =>
                numericFormatter(String(row.factorCommission), {
                  thousandSeparator: ' ',
                }),
              totalKey: 'factorCommission',
              align: 'right',
              headerAlign: 'right',
            },
            {
              render: (row) => (
                <Link
                  to={`/factor/main/deals/${row.unique}?backUrl=${backUrl}`}
                >
                  <IconButton
                    sx={{
                      rotate: '180deg',
                    }}
                  >
                    <ArrowLeftIcon />
                  </IconButton>
                </Link>
              ),
            },
          ]}
          PaginationProps={{
            count: data?.totalPages || 0,
            page: Number(filters.page),
            onChange: (_e, page) => {
              updateFilters({ page });
            },
            sizes: {
              onRowsPerPageChange: (pageSize) =>
                updateFilters({ pageSize: Number(pageSize), page: 1 }),
              currentRowsPerPage: String(filters.pageSize),
            },
          }}
        />
      </EmptyDataPlaceholder>
    </Stack>
  );
};
